import { E2eTestElementId } from '@upright/test-ids/dom-elements';
import UprightLogo from '@upright/ui/common/generic/components/icons/logo/logo-platform-short.svg';
import { getTextStyle } from '@upright/ui/common/generic/content/texts/bou-text/text-style';
import { RemoveScroll } from 'react-remove-scroll';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useIsFullMaintenanceMode } from '../../../../../views/root/context/use-is-maintenance-mode';
import { useGetLoggedInUser } from '@upright/ui/common/generic/hooks';
import { isDmaUser } from '@upright/common/utils';
import { HeaderSearch } from '../../../../upright/model/output/search/header-search';
import { BouColor } from '../../../global/bou-colors';
import { HEADER_HEIGHT_PX } from '../../../layout/layout-constants';
import { fluidSpaceLg } from '../../../utilities/fluid/fluid';
import { handleSearchOptionChange } from '../handle-option-change';
import { HeaderRightItems } from './items';
import { ToolsMenu } from './items/tools-menu';
import { WorkspaceDropdown } from '@upright/ui/views/shared/workspace-dropdown';

export function DesktopHeader() {
  const isFullMaintenance = useIsFullMaintenanceMode();
  const loggedInUser = useGetLoggedInUser();
  const isDmaWorkspaceUser = isDmaUser(loggedInUser);
  const navigate = useNavigate();
  return (
    <>
      {/* HeaderReleaseSelector uses Radix which uses a lib called react-remove-scroll under the hood.
          The lib removes the scrollbar when the Radix Select component is active, which causes fixed elements to jump around.
          We use a helper class provided by the lib to prevent this. See https://github.com/radix-ui/primitives/discussions/1586
       */}
      <Root className={RemoveScroll.classNames.fullWidth}>
        <Content>
          <HeaderLeft>
            <Logo />
            {isDmaWorkspaceUser && <WorkspaceDropdown variant="dark" />}
            <SearchWrap data-testid={E2eTestElementId.DESKTOP_SEARCH} role="search">
              {isFullMaintenance ? (
                <></>
              ) : (
                <HeaderSearch
                  theme="dark"
                  onChange={value => {
                    handleSearchOptionChange(navigate, value);
                  }}
                />
              )}
            </SearchWrap>
            <ToolsMenu />
          </HeaderLeft>
          <HeaderRight>
            <HeaderRightItems />
          </HeaderRight>
        </Content>
      </Root>
      <VerticalSpacer />
    </>
  );
}

export function Logo() {
  return (
    <LogoPart>
      <Link data-testid={E2eTestElementId.LOGO} to="/">
        <DesktopLogo />
      </Link>
    </LogoPart>
  );
}

const Root = styled.div`
  ${getTextStyle({ size: 'xsmall' })}
  position: fixed;
  width: 100vw;
  z-index: 1000;
  height: ${HEADER_HEIGHT_PX};
  padding: 0 1em;
  background-color: ${BouColor.PLATFORM_BG_BLACK};
  color: ${BouColor.PLATFORM_TEXT_WHITE};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Content = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
`;

const LogoPart = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  flex-shrink: 0;
  flex-grow: 0;
`;

const aspectRatio = 14 / 100;
const width = 110;
const height = width * aspectRatio;
const DesktopLogo = styled(UprightLogo)`
  ${fluidSpaceLg('width', width)};
  ${fluidSpaceLg('height', height)};
  padding: 0;
  margin: 0;
  color: ${BouColor.PLATFORM_ICON_WHITE};
`;

const SearchWrap = styled.div``;

// OA: This one will go behind the header so that real content won't.
const VerticalSpacer = styled.div`
  ${getTextStyle({ size: 'xsmall' })};
  height: ${HEADER_HEIGHT_PX};
`;
